/* ManageDeviceViewComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./ManageDeviceViewRenderer");
const PagingDeviceFeed = require("./PagingDeviceFeed");
const _ = require("lodash");

const PlatformSdk = require("@gluenetworks/platform-sdk-remote");
const OsUpgradePlanEntity = PlatformSdk.entities.ws.OsUpgradePlan;

class ManageDeviceViewComponent extends EventEmitter {
    constructor(planDetails) {
        super();
        this._planDetails = planDetails;
        this._planDetails.childViewOpen = true;
        this._gameEditor = planDetails.getGameEditor();
        this._modalUserFeedback = planDetails._modalUserFeedback;
        this._pageSize = 100;
        this._gridKey = "manage_device";
        this._logger = planDetails._logger;
        this._plan = planDetails.getPlan();
        this._isDirty = false;
        this._selectedIDs = undefined;
        this._userGridData = undefined;
        this._deviceFeedSystemQuery = {managementState: {$in: ["MANAGED"]}};
        this._renderer = new Renderer(this);
        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    shutdown() {
        return this.isDirtyChecked()
            .then(() =>
                Promise.all([
                    this._availableDevicesPagingFeed
                        ? this._availableDevicesPagingFeed.shutdown()
                        : undefined,
                    this._selectedDevicesPagingFeed
                        ? this._selectedDevicesPagingFeed.shutdown()
                        : undefined,
                ])
            )
            .then(() => {
                this.emit("Shutdown");
                this._unregisterListeners();
                this._availableDevicesPagingFeed = undefined;
                this._selectedDevicesPagingFeed = undefined;
                this._selectedIDs = undefined;
                this._userGridData = undefined;
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    get isDirty() {
        return this._isDirty;
    }

    set isDirty(newValue) {
        this._isDirty = newValue;
        return true;
    }
    get selectedIDs() {
        return this._selectedIDs;
    }

    set selectedIDs(newValue) {
        this._selectedIDs = newValue;
        return true;
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get getPlanDetailsView() {
        return this._plan;
    }

    get availColumnConfig() {
        return {
            name: {
                label: "Name",
                entityColumn: "name",
                width: 200,
            },
            description: {
                label: "Description",
                entityColumn: "description",
                width: 150,
            },
            detectedSku: {
                label: "SKUs",
                entityColumn: "detectedSku",
                width: 100,
            },
            siteCodeName: {
                label: "Site Code Name",
                entityColumn: "siteCodeName",
                width: 120,
            },
        };
    }

    get selColumnConfig() {
        return {
            name: {
                label: "Name",
                entityColumn: "name",
                width: 200,
            },
            description: {
                label: "Description",
                entityColumn: "description",
                width: 150,
            },
            detectedSku: {
                label: "SKUs",
                entityColumn: "detectedSku",
                width: 100,
            },
            siteCodeName: {
                label: "Site Code Name",
                entityColumn: "siteCodeName",
                width: 120,
            },
        };
    }

    get availableTargetsPagingFeed() {
        return this._availableDevicesPagingFeed;
    }
    get selectedTargetsPagingFeed() {
        return this._selectedDevicesPagingFeed;
    }
    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }
    wasFeedPreviouslyInitialized() {
        return this._availableDevicesPagingFeed.wasPreviouslyInitialized();
    }

    whenModelIsReady() {
        return this._planDetails._storageFactory
            .whenStorageIsReady()
            .then(() => {
                this._userGridData =
                    this._planDetails._storageFactory.getRemoteStorageInstanceForEntityGrid(
                        "entity_grid_gl",
                        `${this._gridKey}_available`
                    );
            })
            .then(() => OsUpgradePlanEntity.getOsUpgradeDeviceQuery(this._gameEditor.activeOrgId))
            .then((result) => {
                if (_.isEmpty(result)) {
                    this._deviceFeedSystemQuery = {
                        $and: [
                            {
                                discoveryStatus: "DISCOVERED",
                            },
                            {
                                managementState: "MANAGED",
                            },
                        ],
                    };
                } else {
                    this._deviceFeedSystemQuery = result;
                }

                this._createAvailPagingFeed();
                this._createSelPagingFeed();
                return Promise.resolve();
            })
            .then(() => {
                return Promise.all([
                    this._getWorkTargetIds(),
                    this._availableDevicesPagingFeed.whenFeedReady(),
                    this._selectedDevicesPagingFeed.whenFeedReady(),
                ]);
            });
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onShutdown() {
        this.shutdown();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _getWorkTargetIds() {
        return this._plan
            .loadRelatedDevices(undefined, {id: true}, undefined, undefined)
            .then((results) => {
                if (results.length > 0) {
                    let resultID = results.map((result) => {
                        return result.id;
                    });
                    this._selectedIDs = resultID;
                } else {
                    this._selectedIDs = [];
                }
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ManageDeviceViewComponent._getWorkTargetIds() error."
                );
                this._selectedIDs = [];
                return Promise.reject(err);
            });
    }
    _createAvailPagingFeed() {
        if (this._availableDevicesPagingFeed) {
            this._availableDevicesPagingFeed.shutdown();
        }
        this._availableDevicesPagingFeed = this._createPagingFeed(
            this.availColumnConfig,
            "$nin",
            `${this._gridKey}_available`,
            true
        );
    }

    _createSelPagingFeed() {
        if (this._selectedDevicesPagingFeed) {
            this._selectedDevicesPagingFeed.shutdown();
        }
        this._selectedDevicesPagingFeed = this._createPagingFeed(
            this.selColumnConfig,
            "$in",
            `${this._gridKey}_selected`,
            false
        );
    }

    _createPagingFeed(columnConfig, mongoIdOperator, feedKey, isFilter) {
        return new PagingDeviceFeed({
            activeOrgId: this._gameEditor.activeOrgId,
            entityDef: this._gameEditor.getDeviceEntity(),
            entityType: "Device",
            columnConfig: columnConfig,
            pageSize: this._pageSize,
            mongoIdOperator: mongoIdOperator,
            sort: {name: 1},
            filter: isFilter
                ? _.get(this._userGridData.getBasicSearchData(), "filterQuery", {})
                : {},
            systemQuery: this._deviceFeedSystemQuery,
            feedKey,
        });
    }
    _saveDevice() {
        const progress = this._startSaveProgress();
        return this._plan
            .updateRelationships()
            .then((result) => {
                this._markDraftStatusAsSaved();
                return progress.whenShown
                    .then(() => progress.complete())
                    .then(() => this._modalUserFeedback.hideDisplayed())
                    .then(() => result);
            })
            .catch((err) => {
                progress.stop();
                this._logger.warn({error: err}, "ManageDeviceViewComponent.saveDevice() error.");
                return progress.whenShown.then(() => {
                    this._modalUserFeedback.displayError(err);
                    return Promise.reject(err);
                });
            });
    }

    /**
     * Shows the save veil and drives its progress line from the parent view's
     * related-device feed while the backend applies the relationship changes.
     *
     * @returns {{whenShown: Promise, stop: function, complete: function}}
     */
    _startSaveProgress() {
        const feed = _.get(this, "_planDetails._pagingRelatedFeed");
        const goal = _.size(this._selectedIDs);
        const start = _.get(feed, "totalEntities", goal);
        const span = Math.abs(goal - start);
        let lastPct = 0;
        let onRefresh;

        const whenShown = this._modalUserFeedback.displayLoading({
            message: "Saving device changes...",
        });

        const showPct = (pct) => {
            lastPct = Math.max(lastPct, Math.min(100, pct));
            this._modalUserFeedback.showLoadingProgress(`${lastPct}% complete`);
        };

        // Adds and removes that cancel out leave the total unchanged, so no
        // percentage can be derived: keep the spinner-only veil in that case.
        if (feed && span > 0) {
            onRefresh = () => {
                const remaining = Math.abs(goal - feed.totalEntities);
                showPct(Math.round((1 - remaining / span) * 100));
            };
            feed.on("RefreshLinesNumber", onRefresh);
            whenShown.then(() => showPct(lastPct));
        }

        const stop = () => {
            if (onRefresh) {
                feed.removeListener("RefreshLinesNumber", onRefresh);
                onRefresh = undefined;
            }
        };

        return {
            whenShown,
            stop,
            complete: () => {
                stop();
                if (span > 0) {
                    showPct(100);
                }
            },
        };
    }

    _discardDeviceChanges() {
        this._plan.clearPendingRelationships();
        let pristineStatus = _.get(this._plan, "pristineProperties.status");
        if (!_.isUndefined(pristineStatus)) {
            this._plan.setStatus(pristineStatus);
        }
    }

    _markDraftStatusAsSaved() {
        let draftStatus = this._plan.getStatusValues().DRAFT;
        if (_.get(this._plan, "pristineProperties.status") === draftStatus) {
            return;
        }
        this._plan._setPristineProperty("status", draftStatus);
        this._plan.setStatus(draftStatus);
    }

    _removeDevices(ids) {
        return this._plan.removeDevicesFromPlan(ids);
    }
    _addDevices(ids) {
        return this._plan.addDevicesToPlan(ids);
    }
    isDirtyChecked() {
        if (this.isDirty) {
            return this._modalUserFeedback
                .displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    dontHide: true,
                    confirmDetails: {
                        text: "Save",
                        type: "additive",
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text",
                    },
                })
                .then(() => {
                    return this._saveDevice().then(() => undefined); // it will not close the tab if it will have something in result
                })
                .catch((err) => {
                    if (_.get(err, "userRejected")) {
                        return Promise.resolve()
                            .then(() => {
                                this._discardDeviceChanges();
                            })
                            .then(() => this._modalUserFeedback.hideDisplayed())
                            .then(() => undefined);
                    }
                    // Save failed: _saveDevice() already showed the error veil. Reject so
                    // the view stays open instead of navigating away from the error.
                    return Promise.reject(err);
                });
        } else {
            return Promise.resolve();
        }
    }
    /*******************************************/
    /* END PRIVATE METHODS                     */
    /*******************************************/
}
module.exports = ManageDeviceViewComponent;
